import { Invoice, User } from '../types';
import { normalizePaymentType } from './paymentDisplay';
import { isActiveTenant } from './tenantStatus';

type MonthStamp = { y: number; m: number };
type StaySegment = { start: MonthStamp; end: MonthStamp };

export const PAYMENT_MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export type TenantPaymentFilter =
  | 'all'
  | 'current_paid'
  | 'current_unpaid'
  | 'due'
  | 'prev_unpaid'
  | 'advance_unpaid';

export function currentRentPeriod(): { month: string; year: string } {
  const now = new Date();
  return {
    month: PAYMENT_MONTH_NAMES[now.getMonth()],
    year: String(now.getFullYear()),
  };
}

/** Canonical month name for matching invoices (case-insensitive). */
export function normalizePaymentMonth(month: string | undefined | null): string {
  const raw = String(month ?? '').trim();
  if (!raw) return '';
  const lower = raw.toLowerCase();
  const exact = PAYMENT_MONTH_NAMES.find((m) => m.toLowerCase() === lower);
  if (exact) return exact;
  const short = PAYMENT_MONTH_NAMES.find((m) => m.toLowerCase().startsWith(lower));
  return short || raw;
}

/** Canonical 4-digit year for matching invoices (handles legacy "24" → "2024"). */
export function normalizePaymentYear(year: string | number | undefined | null): string {
  const raw = String(year ?? '').trim();
  if (/^\d{4}$/.test(raw)) return raw;
  if (/^\d{2}$/.test(raw)) {
    const n = Number(raw);
    return n < 50 ? `20${raw.padStart(2, '0')}` : `19${raw}`;
  }
  return raw;
}

export function rentPeriodKey(month: string, year: string | number): string {
  return `${normalizePaymentMonth(month)}|${normalizePaymentYear(year)}`;
}

function parseMonthStamp(value: string | undefined | null): MonthStamp | null {
  if (value == null || String(value).trim() === '') return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return { y: d.getFullYear(), m: d.getMonth() };
}

function currentMonthStamp(): MonthStamp {
  const now = new Date();
  return { y: now.getFullYear(), m: now.getMonth() };
}

function compareMonths(a: MonthStamp, b: MonthStamp): number {
  if (a.y !== b.y) return a.y - b.y;
  return a.m - b.m;
}

function addMonthsToStamp(stamp: MonthStamp, delta: number): MonthStamp {
  const d = new Date(stamp.y, stamp.m + delta, 1);
  return { y: d.getFullYear(), m: d.getMonth() };
}

function monthsBetween(a: MonthStamp, b: MonthStamp): number {
  return (b.y - a.y) * 12 + (b.m - a.m);
}

function stampFromPeriod(month: string, year: string | number): MonthStamp | null {
  const mName = normalizePaymentMonth(month);
  const mi = PAYMENT_MONTH_NAMES.indexOf(mName);
  const y = Number(normalizePaymentYear(year));
  if (mi < 0 || !Number.isFinite(y)) return null;
  return { y, m: mi };
}

function stampToPeriodKey(stamp: MonthStamp): string {
  return rentPeriodKey(PAYMENT_MONTH_NAMES[stamp.m], String(stamp.y));
}

function enumerateMonthStamps(start: MonthStamp, endInclusive: MonthStamp): MonthStamp[] {
  const out: MonthStamp[] = [];
  let cur = start;
  while (compareMonths(cur, endInclusive) <= 0) {
    out.push(cur);
    cur = addMonthsToStamp(cur, 1);
  }
  return out;
}

function uniqueSortedStamps(stamps: MonthStamp[]): MonthStamp[] {
  const seen = new Set<string>();
  const out: MonthStamp[] = [];
  for (const s of [...stamps].sort(compareMonths)) {
    const key = stampToPeriodKey(s);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
  }
  return out;
}

function clusterInvoiceMonths(stamps: MonthStamp[]): MonthStamp[][] {
  if (!stamps.length) return [];
  const clusters: MonthStamp[][] = [[stamps[0]]];
  for (let i = 1; i < stamps.length; i += 1) {
    const prev = stamps[i - 1];
    const cur = stamps[i];
    if (monthsBetween(prev, cur) <= 2) {
      clusters[clusters.length - 1].push(cur);
    } else {
      clusters.push([cur]);
    }
  }
  return clusters;
}

function resolveStayEndMonth(tenant: User): MonthStamp {
  const now = currentMonthStamp();
  if (!isActiveTenant(tenant)) {
    const left = parseMonthStamp(tenant.dateOfLeft || tenant.leavingDate);
    return left ?? addMonthsToStamp(now, -120);
  }
  if (tenant.isNotice && tenant.leavingDate) {
    const leaving = parseMonthStamp(tenant.leavingDate);
    if (leaving && compareMonths(leaving, now) <= 0) return leaving;
  }
  return now;
}

function collectRentInvoiceMonths(invoices: Invoice[]): MonthStamp[] {
  const stamps: MonthStamp[] = [];
  for (const inv of invoices) {
    if (inv.isDeleted) continue;
    if (normalizePaymentType(inv.paymentType) !== 'RENT') continue;
    const stamp = stampFromPeriod(inv.paymentMonth, inv.paymentYear);
    if (stamp) stamps.push(stamp);
  }
  return uniqueSortedStamps(stamps);
}

function mergeStaySegments(segments: StaySegment[]): StaySegment[] {
  if (!segments.length) return [];
  const sorted = [...segments].sort((a, b) => compareMonths(a.start, b.start));
  const merged: StaySegment[] = [{ ...sorted[0] }];
  for (let i = 1; i < sorted.length; i += 1) {
    const prev = merged[merged.length - 1];
    const cur = sorted[i];
    if (monthsBetween(prev.end, cur.start) <= 1) {
      if (compareMonths(cur.end, prev.end) > 0) prev.end = cur.end;
    } else {
      merged.push({ ...cur });
    }
  }
  return merged;
}

/** Stay windows derived from join/leave dates and rent-invoice history (handles rejoin gaps). */
export function getTenantStaySegments(tenant: User, invoices: Invoice[]): StaySegment[] {
  const join = parseMonthStamp(tenant.dateOfJoining);
  const stayEnd = resolveStayEndMonth(tenant);
  const active = isActiveTenant(tenant);
  const activeInvoices = invoices.filter((i) => !i.isDeleted);
  const invoiceMonths = collectRentInvoiceMonths(activeInvoices);
  const segments: StaySegment[] = [];

  for (const cluster of clusterInvoiceMonths(invoiceMonths)) {
    let start = cluster[0];
    const clusterEnd = cluster[cluster.length - 1];
    if (
      join
      && compareMonths(join, start) > 0
      && compareMonths(join, clusterEnd) <= 0
    ) {
      start = join;
    }
    const end = compareMonths(clusterEnd, stayEnd) <= 0 ? clusterEnd : stayEnd;
    if (compareMonths(start, end) <= 0) {
      segments.push({ start, end });
    }
  }

  if (active && join && compareMonths(join, stayEnd) <= 0) {
    const lastInv = invoiceMonths[invoiceMonths.length - 1];
    if (!lastInv) {
      segments.push({ start: join, end: stayEnd });
    } else if (compareMonths(join, lastInv) > 0) {
      segments.push({ start: join, end: stayEnd });
    } else if (segments.length) {
      const last = segments[segments.length - 1];
      if (compareMonths(stayEnd, last.end) > 0) {
        last.end = stayEnd;
      }
    }
  } else if (!active && join && compareMonths(join, stayEnd) <= 0 && !segments.length) {
    segments.push({ start: join, end: stayEnd });
  }

  return mergeStaySegments(segments);
}

export function getBillableRentMonthKeys(tenant: User, invoices: Invoice[]): Set<string> {
  const keys = new Set<string>();
  for (const seg of getTenantStaySegments(tenant, invoices)) {
    for (const stamp of enumerateMonthStamps(seg.start, seg.end)) {
      keys.add(stampToPeriodKey(stamp));
    }
  }
  return keys;
}

export function isRentMonthBillable(
  tenant: User,
  month: string,
  year: string | number,
  invoices: Invoice[],
): boolean {
  const stamp = stampFromPeriod(month, year);
  if (!stamp) return false;
  return getBillableRentMonthKeys(tenant, invoices).has(stampToPeriodKey(stamp));
}

function matchesRentPeriod(inv: Invoice, month: string, year: string): boolean {
  if (normalizePaymentType(inv.paymentType) !== 'RENT' || inv.isDeleted) return false;
  return rentPeriodKey(inv.paymentMonth, inv.paymentYear) === rentPeriodKey(month, year);
}

/** All RENT invoices for a month/year with summed paid & balance. */
export function aggregateRentForPeriod(
  invoices: Invoice[],
  month: string,
  year: string
): {
  /** Cash collected only (excludes discount). */
  totalPaid: number;
  /** Discounts only (separate from paid). */
  totalDiscount: number;
  /** Paid + discount — used for fully-paid / balance-due checks only. */
  totalCredited: number;
  totalBalance: number;
  invoices: Invoice[];
  primaryInvoice?: Invoice;
} {
  const matches = invoices.filter((i) => matchesRentPeriod(i, month, year));
  let totalPaid = 0;
  let totalDiscount = 0;
  let totalBalance = 0;
  for (const inv of matches) {
    const paid = parseFloat(String(inv.paidAmount || '0'));
    const discount = parseFloat(String(inv.discount || '0'));
    const bal = parseFloat(String(inv.balanceAmount || '0'));
    if (Number.isFinite(paid)) totalPaid += paid;
    if (Number.isFinite(discount)) totalDiscount += discount;
    if (Number.isFinite(bal)) totalBalance += bal;
  }
  const totalCredited = totalPaid + totalDiscount;

  let primaryInvoice: Invoice | undefined;
  const withBalance = matches.filter(
    (i) => parseFloat(String(i.balanceAmount || '0')) > 0.01
  );
  if (withBalance.length) {
    primaryInvoice = [...withBalance].sort(
      (a, b) =>
        parseFloat(String(b.balanceAmount || '0')) -
        parseFloat(String(a.balanceAmount || '0'))
    )[0];
  } else if (matches.length) {
    primaryInvoice = [...matches].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0];
  }

  return {
    totalPaid,
    totalDiscount,
    totalCredited,
    totalBalance,
    invoices: matches,
    primaryInvoice,
  };
}

export function getRentPeriodBalanceDue(
  tenant: User,
  invoices: Invoice[],
  month: string,
  year: string
): number {
  const rent = tenantRentAmount(tenant);
  const active = invoices.filter((i) => !i.isDeleted);
  const agg = aggregateRentForPeriod(active, month, year);
  if (!agg.invoices.length) return rent > 0 ? rent : 0;
  if (rent <= 0) return Math.max(0, agg.totalBalance);
  return Math.max(0, rent - agg.totalCredited);
}

export function isRentPeriodFullyPaid(
  tenant: User,
  invoices: Invoice[],
  month: string,
  year: string
): boolean {
  const active = invoices.filter((i) => !i.isDeleted);
  const agg = aggregateRentForPeriod(active, month, year);
  if (!agg.invoices.length) return false;

  if (agg.totalBalance <= 0.01 && agg.totalCredited > 0.01) return true;

  if (
    agg.totalBalance <= 0.01
    && agg.invoices.some(
      (i) =>
        String(i.paymentStatus || '').toUpperCase() === 'PAID'
        && Boolean(i.paymentTimestamp || i.paymentMonth)
    )
  ) {
    return true;
  }

  const rent = tenantRentAmount(tenant);
  if (rent <= 0) {
    return agg.invoices.some(
      (i) => String(i.paymentStatus || '').toUpperCase() === 'PAID'
    );
  }
  return agg.totalCredited >= rent - 0.01;
}

/** Invoice to link payments — highest balance for period, else latest. */
export function pickBestRentInvoiceForPeriod(
  invoices: Invoice[],
  month: string,
  year: string,
  _tenant?: User
): Invoice | undefined {
  const active = invoices.filter((i) => !i.isDeleted);
  return aggregateRentForPeriod(active, month, year).primaryInvoice;
}

export function getRentInvoiceForPeriod(
  invoices: Invoice[],
  month: string,
  year: string,
  tenant?: User
): Invoice | undefined {
  if (tenant) {
    return pickBestRentInvoiceForPeriod(invoices, month, year, tenant);
  }
  return invoices.find((i) => matchesRentPeriod(i, month, year));
}

export function tenantRentAmount(tenant: User): number {
  const rent = Number(tenant.rent);
  return Number.isFinite(rent) && rent > 0 ? rent : 0;
}

/** @deprecated Prefer isRentPeriodFullyPaid with all invoices for the month. */
export function isRentFullyPaid(tenant: User, inv: Invoice | undefined): boolean {
  const rent = tenantRentAmount(tenant);
  if (rent <= 0 || !inv) return false;
  const paid = parseFloat(String(inv.paidAmount || '0'));
  const discount = parseFloat(String(inv.discount || '0'));
  const credited = (Number.isFinite(paid) ? paid : 0) + (Number.isFinite(discount) ? discount : 0);
  return credited >= rent - 0.01;
}

export function hasCurrentMonthRentDue(tenant: User, invoices: Invoice[]): boolean {
  const { month, year } = currentRentPeriod();
  const active = invoices.filter((i) => !i.isDeleted);
  if (!isRentMonthBillable(tenant, month, year, active)) return false;
  return getRentPeriodBalanceDue(tenant, active, month, year) > 0.01;
}

export type CurrentMonthRentStatus = {
  month: string;
  year: string;
  periodLabel: string;
  rent: number;
  isFullyPaid: boolean;
  balanceDue: number;
  totalPaid: number;
  totalDiscount: number;
  totalCredited: number;
};

export function getCurrentMonthRentStatus(tenant: User, invoices: Invoice[]): CurrentMonthRentStatus | null {
  const rent = tenantRentAmount(tenant);
  if (rent <= 0) return null;

  const { month, year } = currentRentPeriod();
  const active = invoices.filter((i) => !i.isDeleted);
  if (!isRentMonthBillable(tenant, month, year, active)) {
    return {
      month,
      year,
      periodLabel: `${month} ${year}`,
      rent,
      isFullyPaid: true,
      balanceDue: 0,
      totalPaid: 0,
      totalDiscount: 0,
      totalCredited: 0,
    };
  }
  const agg = aggregateRentForPeriod(active, month, year);
  const isFullyPaid = isRentPeriodFullyPaid(tenant, active, month, year);
  const balanceDue = isFullyPaid
    ? 0
    : getRentPeriodBalanceDue(tenant, active, month, year);

  return {
    month,
    year,
    periodLabel: `${month} ${year}`,
    rent,
    isFullyPaid,
    balanceDue,
    totalPaid: agg.totalPaid,
    totalDiscount: agg.totalDiscount,
    totalCredited: agg.totalCredited,
  };
}

export function hasPreviousMonthRentUnpaid(tenant: User, invoices: Invoice[]): boolean {
  return computeUnpaidRentMonthsBeforeCurrent(tenant, invoices).length > 0;
}

export function hasAdvanceUnpaid(invoices: Invoice[]): boolean {
  return invoices.some((i) => {
    if (normalizePaymentType(i.paymentType) !== 'ADVANCE' || i.isDeleted) return false;
    const balance = parseFloat(String(i.balanceAmount || '0'));
    if (balance > 0.01) return true;
    const status = i.paymentStatus;
    return status === 'PENDING' || status === 'PARTIAL' || status === 'OVERDUE';
  });
}

export function computeUnpaidRentMonthsBeforeCurrent(tenant: User, invoices: Invoice[]): {
  month: string;
  year: string;
  periodLabel: string;
  rent: number;
  balanceDue: number;
  existingInvoiceId?: number;
}[] {
  const rent = tenantRentAmount(tenant);
  if (rent <= 0) return [];

  const activeInvoices = invoices.filter((i) => !i.isDeleted);
  const billableMonths = getBillableRentMonthKeys(tenant, activeInvoices);
  if (!billableMonths.size) return [];

  const now = new Date();
  const endExclusive = new Date(now.getFullYear(), now.getMonth(), 1);
  const segments = getTenantStaySegments(tenant, activeInvoices);
  if (!segments.length) return [];

  const rentByPeriod = new Map<string, Invoice[]>();
  for (const inv of activeInvoices) {
    if (normalizePaymentType(inv.paymentType) !== 'RENT') continue;
    const key = rentPeriodKey(inv.paymentMonth, inv.paymentYear);
    if (!rentByPeriod.has(key)) rentByPeriod.set(key, []);
    rentByPeriod.get(key)!.push(inv);
  }

  const rows: {
    month: string;
    year: string;
    periodLabel: string;
    rent: number;
    balanceDue: number;
    existingInvoiceId?: number;
  }[] = [];

  for (const seg of segments) {
    for (const stamp of enumerateMonthStamps(seg.start, seg.end)) {
      const cursor = new Date(stamp.y, stamp.m, 1);
      if (cursor >= endExclusive) continue;

      const month = PAYMENT_MONTH_NAMES[stamp.m];
      const year = String(stamp.y);
      const periodKey = rentPeriodKey(month, year);
      if (!billableMonths.has(periodKey)) continue;

      const periodInvs = rentByPeriod.get(periodKey) || [];
      const fullyPaid = isRentPeriodFullyPaid(tenant, periodInvs, month, year);

      if (!fullyPaid) {
        const balanceDue = getRentPeriodBalanceDue(tenant, periodInvs, month, year);
        const primary = aggregateRentForPeriod(periodInvs, month, year).primaryInvoice;
        rows.push({
          month,
          year,
          periodLabel: `${month} ${year}`,
          rent,
          balanceDue,
          existingInvoiceId: primary?.id,
        });
      }
    }
  }

  rows.sort((a, b) => {
    const sa = stampFromPeriod(a.month, a.year);
    const sb = stampFromPeriod(b.month, b.year);
    if (!sa || !sb) return 0;
    return compareMonths(sa, sb);
  });
  return rows;
}

export function matchesTenantPaymentFilter(
  tenant: User,
  filter: TenantPaymentFilter,
  invoicesByUser: Map<number, Invoice[]>
): boolean {
  if (filter === 'all') return true;
  const invs = invoicesByUser.get(tenant.id) || [];
  const { month, year } = currentRentPeriod();
  const rent = tenantRentAmount(tenant);
  const active = invs.filter((i) => !i.isDeleted);

  switch (filter) {
    case 'current_paid':
      return (
        !isRentMonthBillable(tenant, month, year, active)
        || isRentPeriodFullyPaid(tenant, active, month, year)
      );
    case 'current_unpaid':
      return (
        rent > 0
        && isRentMonthBillable(tenant, month, year, active)
        && !isRentPeriodFullyPaid(tenant, active, month, year)
      );
    case 'due':
      return hasCurrentMonthRentDue(tenant, active);
    case 'prev_unpaid':
      return hasPreviousMonthRentUnpaid(tenant, invs);
    case 'advance_unpaid':
      return hasAdvanceUnpaid(invs);
    default:
      return true;
  }
}

/** Total unpaid rent (prior + current) and advance balance for a tenant. */
export function getTenantOutstandingAmount(tenant: User, invoices: Invoice[]): number {
  if (!isActiveTenant(tenant)) return 0;
  const activeInvs = invoices.filter((i) => !i.isDeleted);
  const current = getCurrentMonthRentStatus(tenant, activeInvs);
  const previousUnpaid = computeUnpaidRentMonthsBeforeCurrent(tenant, activeInvs).reduce(
    (sum, row) => sum + row.balanceDue,
    0,
  );
  const currentMonthDue = current?.balanceDue ?? 0;
  let advanceBalance = 0;
  for (const inv of activeInvs) {
    if (normalizePaymentType(inv.paymentType) !== 'ADVANCE') continue;
    const balance = parseFloat(String(inv.balanceAmount || '0'));
    if (Number.isFinite(balance) && balance > 0) advanceBalance += balance;
  }
  return previousUnpaid + currentMonthDue + advanceBalance;
}

export function tenantHasPendingDues(tenant: User, invoices: Invoice[]): boolean {
  return getTenantOutstandingAmount(tenant, invoices) > 0.01;
}

export function groupInvoicesByUserId(invoices: Invoice[]): Map<number, Invoice[]> {
  const map = new Map<number, Invoice[]>();
  for (const inv of invoices) {
    const uid = inv.userId;
    if (!map.has(uid)) map.set(uid, []);
    map.get(uid)!.push(inv);
  }
  return map;
}
